export function PrivacyPage() {
  return (
    <div className="legal-site">
      <header className="legal-header">
        <a href="/" aria-label="Stalkie home">Stalkie</a>
        <span>Privacy</span>
      </header>
      <main className="legal-main">
        <div className="legal-heading">
          <p>Legal and privacy</p>
          <h1>Privacy Policy</h1>
          <span>How Stalkie handles account, monitoring and billing data.</span>
        </div>

        <article className="legal-document">
          <p>
            <strong>Last updated:</strong> March 14, 2026
          </p>
          <p>
            Stalkie built this app as a freemium service. This Service is provided
            by Stalkie and is intended for use as-is.
          </p>
          <p>
            This page explains how Stalkie collects, uses, and shares information
            when you use the app.
          </p>
          <p>By using Stalkie, you agree to this Privacy Policy.</p>

          <h2 className="text-xl font-semibold text-gray-900 mt-8">Information we collect</h2>
          <p>Stalkie may collect:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Account and device identifiers used to run the service.</li>
            <li>
              Monitoring data, such as tracked accounts and detected follow or
              unfollow events.
            </li>
            <li>
              Subscription status and billing events from Apple, Google,
              RevenueCat (mobile), and Stripe (web).
            </li>
            <li>Push notification token data needed to deliver alerts.</li>
            <li>Technical diagnostics, crash reports, and performance data.</li>
          </ul>
          <p>Stalkie does not ask for or store your Instagram password.</p>

          <h2 className="text-xl font-semibold text-gray-900 mt-8">How we use information</h2>
          <p>Stalkie uses information to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>provide and maintain the service;</li>
            <li>show monitoring results and event history;</li>
            <li>send notifications and app updates;</li>
            <li>validate subscriptions and paid feature access;</li>
            <li>detect abuse, fraud, and technical issues;</li>
            <li>improve app reliability and experience.</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-900 mt-8">Third-party services</h2>
          <p>
            Stalkie uses third-party providers to operate the app. These may
            include:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>infrastructure and database providers (for example, Convex);</li>
            <li>
              subscription and billing providers (Apple, Google, RevenueCat for
              mobile, and Stripe for web);
            </li>
            <li>notification providers (Expo Push);</li>
            <li>
              analytics, logging, and error monitoring providers (for example,
              Sentry);
            </li>
            <li>AI providers used by specific features.</li>
          </ul>
          <p>
            These providers may process data only to deliver services on Stalkie&apos;s
            behalf.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-8">Log data</h2>
          <p>
            If the app fails, Stalkie may collect technical log data through
            third-party tools. This may include device model, operating system, app
            version, timestamps, error details, and network metadata.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-8">
            Cookies and similar technologies
          </h2>
          <p>
            The web app uses browser local and session storage for authentication
            continuity, language preference, onboarding state, and returning from
            hosted checkout.
          </p>
          <p>
            When configured, optional GA4 and PostHog analytics receive product
            events. After web login, PostHog uses an internal Convex user
            identifier rather than an email address. Stripe Checkout and Customer
            Portal are hosted by Stripe and may use their own cookies and similar
            technologies under Stripe&apos;s policies.
          </p>
          <p>
            The mobile app does not use browser cookies for its core functionality.
            Its Private Account Monitoring feature uses an embedded browser that
            stores Instagram session cookies on your device. These cookies are
            used solely to authenticate your Instagram session and are not shared
            with Stalkie&apos;s servers.
          </p>
          <p>
            Other integrated third-party SDKs may also use similar identifiers to
            support security, diagnostics, or service functionality.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-8">Data retention</h2>
          <p>
            Stalkie keeps data only as long as needed to operate the service, meet
            legal obligations, and protect security.
          </p>
          <p>Example retention window:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Monitoring event history: up to <strong>30 days</strong>.
            </li>
          </ul>
          <p>
            Other operational records may be kept for shorter or longer periods
            based on security and compliance needs.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-8">Security</h2>
          <p>
            Stalkie uses commercially reasonable safeguards to protect your data. No
            method of transmission or storage is 100% secure, so absolute security
            cannot be guaranteed.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-8">Your choices and rights</h2>
          <p>
            Depending on your location, you may have rights to access, correct,
            export, or delete your data.
          </p>
          <p>
            You can request deletion by emailing{" "}
            <strong>
              <a href="mailto:privacy@stalkie.app" className="text-rose-500">
                privacy@stalkie.app
              </a>
            </strong>{" "}
            with subject: <strong>Data Deletion Request</strong>.
          </p>
          <p>Stalkie may ask for account verification before completing requests.</p>
          <p>You can also disable notifications in your device settings at any time.</p>

          <h2 className="text-xl font-semibold text-gray-900 mt-8">Children&apos;s privacy</h2>
          <p>
            Stalkie is not intended for children under 13 (or the minimum age
            required by local law). If Stalkie learns that a child provided personal
            data without required consent, that data will be deleted.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-8">Changes to this policy</h2>
          <p>
            Stalkie may update this Privacy Policy. Any updates are posted on this
            page with a new &quot;Last updated&quot; date.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-8">Contact</h2>
          <p>For privacy questions or requests:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Company/Responsible Party:</strong> Stalkie
            </li>
            <li>
              <strong>Privacy email:</strong>{" "}
              <a href="mailto:privacy@stalkie.app" className="text-rose-500 font-semibold">
                privacy@stalkie.app
              </a>
            </li>
            <li>
              <strong>General support:</strong>{" "}
              <a href="mailto:support@stalkie.app" className="text-rose-500 font-semibold">
                support@stalkie.app
              </a>
            </li>
            <li>
              <strong>Website:</strong>{" "}
              <a href="https://stalkie.app" className="text-rose-500 font-semibold">
                https://stalkie.app
              </a>
            </li>
          </ul>
        </article>
      </main>
      <footer className="legal-footer"><span>© Stalkie</span><a href="/terms">Terms of Use</a></footer>
    </div>
  );
}
